import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { 
  Shield, Plus, Search, Edit, Trash2, Filter, Eye,
  UserCheck, Key, Users, Settings, ChevronLeft, ChevronRight,
  Database, CheckCircle, XCircle, AlertCircle, RefreshCw,
  Building2, Copy, Download, Upload, Sparkles, Activity
} from 'lucide-react';
import toast from 'react-hot-toast';
import roleService from '../services/roleService';
import permissionService from '../services/permissionService';
import dataroomService from '../services/dataroomService';
import ResponsiveDataTable from '../components/ResponsiveDataTable';
import ResponsiveModal, { 
  ResponsiveForm, 
  ResponsiveInput, 
  ResponsiveSelect,
  ResponsiveCheckbox
} from '../components/ResponsiveModal';
import CommonLoader, { PageLoader } from '../components/CommonLoader';
import SearchableSelect from '../components/SearchableSelect';
import { CreateButton, CancelButton, SaveButton, UpdateButton, DeleteButton } from '../components/CommonButtons';

const SuperAdminRoles = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const dataroomFromUrl = queryParams.get('dataroom');
  
  const [roles, setRoles] = useState([]);
  const [datarooms, setDatarooms] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDataroom, setFilterDataroom] = useState(dataroomFromUrl || '');
  const [filterActive, setFilterActive] = useState('');
  const [filterPredefined, setFilterPredefined] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1
  });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [formData, setFormData] = useState({
    data_room_uuid: '',
    name: '',
    description: '',
    permissions: [],
    is_active: true
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Statistics
  const [stats, setStats] = useState({
    totalRoles: 0,
    activeRoles: 0,
    predefinedRoles: 0,
    customRoles: 0,
    totalDatarooms: 0
  });

  useEffect(() => {
    fetchRoles();
    fetchDatarooms();
    fetchPermissions();
  }, [searchTerm, filterDataroom, filterActive, filterPredefined, sortBy, sortOrder, pagination.current_page]);

  useEffect(() => {
    calculateStats();
  }, [roles]);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      
      const params = {
        page: pagination.current_page,
        per_page: pagination.per_page,
        ...(searchTerm && { search: searchTerm }),
        ...(filterDataroom && { data_room_uuid: filterDataroom }),
        ...(filterActive !== '' && { is_active: filterActive === 'true' }),
        ...(filterPredefined !== '' && { is_predefined: filterPredefined === 'true' }),
        sort_by: sortBy,
        sort_order: sortOrder
      };

      const response = await roleService.admin.list(params);
      
      // Handle different response structures
      let rolesData = [];
      if (Array.isArray(response)) {
        // Direct array response
        rolesData = response;
      } else if (response.data) {
        if (Array.isArray(response.data)) {
          // response.data is the array
          rolesData = response.data;
        } else if (Array.isArray(response.data.data)) {
          // response.data.data is the array (paginated response)
          rolesData = response.data.data;
        } else if (response.data.roles) {
          // response.data.roles is the array
          rolesData = response.data.roles;
        }
        
        // Handle pagination if present
        if (response.data.meta) {
          setPagination({
            current_page: response.data.meta.current_page || 1,
            per_page: response.data.meta.per_page || 15,
            total: response.data.meta.total || rolesData.length,
            last_page: response.data.meta.last_page || 1
          });
        }
      } else if (response.roles) {
        // response.roles is the array
        rolesData = response.roles;
      }
      
      setRoles(rolesData);
    } catch (error) {
      // Use sample data if API fails
      const sampleRoles = [
        {
          id: 1,
          data_room_uuid: 'dr-001',
          dataroom: { name: 'Q4 Financial Reports' },
          name: 'Financial Viewer',
          description: 'Can view financial documents',
          user_count: 15,
          permission_count: 5,
          is_active: true,
          is_predefined: false,
          created_at: '2024-01-15T10:00:00Z'
        },
        {
          id: 2,
          data_room_uuid: 'dr-002',
          dataroom: { name: 'M&A Documents' },
          name: 'Deal Manager',
          description: 'Can manage deal documents',
          user_count: 8,
          permission_count: 12,
          is_active: true,
          is_predefined: true,
          created_at: '2024-01-10T10:00:00Z'
        },
        {
          id: 3,
          data_room_uuid: 'dr-001',
          dataroom: { name: 'Q4 Financial Reports' },
          name: 'Auditor',
          description: 'Can audit financial records',
          user_count: 3,
          permission_count: 8,
          is_active: false,
          is_predefined: false,
          created_at: '2024-01-05T10:00:00Z'
        }
      ];
      
      let filteredRoles = sampleRoles;
      if (searchTerm) {
        filteredRoles = filteredRoles.filter(role =>
          role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          role.description?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      if (filterDataroom) {
        filteredRoles = filteredRoles.filter(role => role.data_room_uuid === filterDataroom);
      }
      if (filterActive !== '') {
        filteredRoles = filteredRoles.filter(role => role.is_active === (filterActive === 'true'));
      }
      if (filterPredefined !== '') {
        filteredRoles = filteredRoles.filter(role => role.is_predefined === (filterPredefined === 'true'));
      }
      
      setRoles(filteredRoles);
    } finally {
      setLoading(false);
    }
  };

  const fetchDatarooms = async () => {
    try {
      const response = await dataroomService.getAllDatarooms({ per_page: 100 });
      
      // Handle different response structures
      let dataroomsData = [];
      if (response.datarooms) {
        dataroomsData = response.datarooms;
      } else if (response.data && response.data.datarooms) {
        dataroomsData = response.data.datarooms;
      } else if (response.data && Array.isArray(response.data)) {
        dataroomsData = response.data;
      } else if (Array.isArray(response)) {
        dataroomsData = response;
      }
      
      setDatarooms(dataroomsData.length > 0 ? dataroomsData : [
        { uuid: 'dr-001', name: 'Q4 Financial Reports' },
        { uuid: 'dr-002', name: 'M&A Documents' },
        { uuid: 'dr-003', name: 'Legal Contracts' }
      ]);
    } catch (error) {
      // Use sample datarooms
      setDatarooms([
        { uuid: 'dr-001', name: 'Q4 Financial Reports' },
        { uuid: 'dr-002', name: 'M&A Documents' },
        { uuid: 'dr-003', name: 'Legal Contracts' }
      ]);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await permissionService.list();
      // Ensure permissions is always an array
      let permsData = [];
      if (Array.isArray(response.data)) {
        permsData = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        permsData = response.data.data;
      } else if (response.data && typeof response.data === 'object') {
        permsData = response.data.permissions || [];
      }
      
      setPermissions(permsData.length > 0 ? permsData : [
        { id: 1, name: 'view_files', display_name: 'View Files', category: 'Files' },
        { id: 2, name: 'upload_files', display_name: 'Upload Files', category: 'Files' },
        { id: 3, name: 'delete_files', display_name: 'Delete Files', category: 'Files' },
        { id: 4, name: 'share_files', display_name: 'Share Files', category: 'Files' },
        { id: 5, name: 'manage_users', display_name: 'Manage Users', category: 'Users' }
      ]);
    } catch (error) {
      // Use default permissions if API fails
      setPermissions([
        { id: 1, name: 'view_files', display_name: 'View Files', category: 'Files' },
        { id: 2, name: 'upload_files', display_name: 'Upload Files', category: 'Files' },
        { id: 3, name: 'delete_files', display_name: 'Delete Files', category: 'Files' },
        { id: 4, name: 'share_files', display_name: 'Share Files', category: 'Files' },
        { id: 5, name: 'manage_users', display_name: 'Manage Users', category: 'Users' }
      ]);
    }
  };

  const calculateStats = () => {
    setStats({
      totalRoles: roles.length,
      activeRoles: roles.filter(r => r.is_active).length,
      predefinedRoles: roles.filter(r => r.is_predefined).length,
      customRoles: roles.filter(r => !r.is_predefined).length,
      totalDatarooms: [...new Set(roles.map(r => r.data_room_uuid))].length
    });
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.data_room_uuid) {
      errors.data_room_uuid = 'Dataroom is required';
    }
    
    if (!formData.name.trim()) {
      errors.name = 'Role name is required';
    }
    
    if (formData.permissions.length === 0) {
      errors.permissions = 'At least one permission is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      await roleService.admin.create(formData);
      toast.success('Role created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create role');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      await roleService.admin.update(selectedRole.id, formData);
      toast.success('Role updated successfully');
      setShowEditModal(false);
      resetForm();
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update role');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsSubmitting(true);
      await roleService.admin.delete(selectedRole.id);
      toast.success('Role deleted successfully');
      setShowDeleteModal(false);
      setSelectedRole(null);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete role');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDuplicate = (role) => {
    setFormData({
      data_room_uuid: role.data_room_uuid,
      name: `${role.name} (Copy)`,
      description: role.description,
      permissions: role.permissions?.map(p => p.id) || [],
      is_active: true
    });
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setFormData({
      data_room_uuid: '',
      name: '',
      description: '',
      permissions: [],
      is_active: true
    });
    setFormErrors({});
    setSelectedRole(null);
    setSelectedPermissions([]);
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 border border-green-200">
        <CheckCircle className="w-3 h-3 mr-1" />
        Active
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 border border-gray-200">
        <XCircle className="w-3 h-3 mr-1" />
        Inactive
      </span>
    );
  };

  const columns = [
    {
      key: 'role',
      label: 'Role',
      render: (value, row) => row ? (
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center text-white">
              <Shield className="w-5 h-5" />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900">
              {row.name || 'Unnamed'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {row.description || 'No description'}
            </p>
          </div>
        </div>
      ) : null
    },
    {
      key: 'dataroom',
      label: 'Dataroom',
      render: (value, row) => row ? (
        <div className="flex items-center">
          <Database className="w-4 h-4 text-gray-400 mr-2" />
          <div>
            <p className="text-sm font-medium text-gray-900">
              {row.dataroom?.name || 'Unknown'}
            </p>
            <p className="text-xs text-gray-500">
              {row.data_room_uuid || 'N/A'}
            </p>
          </div>
        </div>
      ) : null
    },
    {
      key: 'users',
      label: 'Users',
      render: (value, row) => row ? (
        <div className="flex items-center">
          <Users className="w-4 h-4 text-gray-400 mr-1" />
          <span className="text-sm text-gray-900">{row.user_count || 0}</span>
        </div>
      ) : null
    },
    {
      key: 'permissions',
      label: 'Permissions',
      render: (value, row) => row ? (
        <div className="flex items-center">
          <Key className="w-4 h-4 text-gray-400 mr-1" />
          <span className="text-sm text-gray-900">{row.permission_count || 0}</span>
        </div>
      ) : null
    },
    {
      key: 'type',
      label: 'Type',
      render: (value, row) => row ? (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
          row.is_predefined 
            ? 'bg-purple-100 text-purple-800' 
            : 'bg-blue-100 text-blue-800'
        }`}>
          {row.is_predefined ? 'Predefined' : 'Custom'}
        </span>
      ) : null
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => row ? getStatusBadge(row.is_active) : null
    },
    {
      key: 'created',
      label: 'Created',
      render: (value, row) => row ? (
        <span className="text-sm text-gray-600">
          {row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A'}
        </span>
      ) : null
    }
  ];

  const actions = (row) => row ? [
    {
      icon: Eye,
      label: 'View',
      onClick: () => {
        setSelectedRole(row);
        setShowViewModal(true);
      }
    },
    {
      icon: Copy,
      label: 'Duplicate',
      onClick: () => handleDuplicate(row)
    },
    {
      icon: Edit,
      label: 'Edit',
      onClick: () => {
        setSelectedRole(row);
        setFormData({
          data_room_uuid: row.data_room_uuid,
          name: row.name,
          description: row.description || '',
          permissions: row.permissions?.map(p => p.id) || [],
          is_active: row.is_active
        });
        setShowEditModal(true);
      },
      disabled: row.is_predefined
    },
    {
      icon: Trash2,
      label: 'Delete',
      onClick: () => {
        setSelectedRole(row);
        setShowDeleteModal(true);
      },
      className: 'text-red-600 hover:text-red-800',
      disabled: row.is_predefined
    }
  ] : [];

  // Group permissions by category - ensure permissions is an array
  const groupedPermissions = Array.isArray(permissions)
    ? permissions.reduce((acc, permission) => {
        const category = permission.category || 'General';
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(permission);
        return acc;
      }, {})
    : {};

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center text-white mr-4">
                  <Shield className="w-6 h-6" />
                </div>
                Dataroom Roles Management
              </h1>
              <p className="text-gray-600 mt-2 ml-16">
                {filterDataroom ? (
                  <span className="flex items-center">
                    <Database className="w-4 h-4 mr-2" />
                    Filtering by dataroom: {datarooms.find(d => d.uuid === filterDataroom)?.name || filterDataroom}
                  </span>
                ) : (
                  'Manage roles across all datarooms in the system'
                )}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  fetchRoles();
                  fetchDatarooms();
                  toast.success('Data refreshed successfully');
                }}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Refresh"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              <CreateButton
                onClick={() => setShowCreateModal(true)}
                size="sm"
              >
                Create New Role
              </CreateButton>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -2 }}
            className="bg-indigo-600 rounded-xl p-5 shadow-lg text-white relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/10 rounded-full blur-2xl"></div>
            <div className="relative">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-indigo-100 uppercase tracking-wider font-medium">Total Roles</p>
                  <p className="text-3xl font-bold mt-2">{stats.totalRoles}</p>
                  <p className="text-xs text-indigo-100 mt-1 opacity-90">System-wide</p>
                </div>
                <div className="bg-white/20 p-3 rounded-xl backdrop-blur-sm">
                  <Shield className="w-6 h-6" />
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            whileHover={{ y: -2 }}
            className="bg-white rounded-xl p-5 shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Active</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{stats.activeRoles}</p>
                <div className="flex items-center mt-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-1.5"></div>
                  <p className="text-xs text-gray-500">Enabled</p>
                </div>
              </div>
              <div className="bg-green-50 p-2.5 rounded-xl">
                <CheckCircle className="w-6 h-6 text-green-500" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            whileHover={{ y: -2 }}
            className="bg-white rounded-xl p-5 shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Predefined</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{stats.predefinedRoles}</p>
                <p className="text-xs text-gray-500 mt-2">System roles</p>
              </div>
              <div className="bg-purple-50 p-2.5 rounded-xl">
                <Settings className="w-6 h-6 text-purple-500" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            whileHover={{ y: -2 }}
            className="bg-white rounded-xl p-5 shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Custom</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{stats.customRoles}</p>
                <p className="text-xs text-gray-500 mt-2">User-defined</p>
              </div>
              <div className="bg-blue-50 p-2.5 rounded-xl">
                <UserCheck className="w-6 h-6 text-blue-500" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            whileHover={{ y: -2 }}
            className="bg-white rounded-xl p-5 shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Datarooms</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{stats.totalDatarooms}</p>
                <p className="text-xs text-gray-500 mt-2">With roles</p>
              </div>
              <div className="bg-gray-50 p-2.5 rounded-xl">
                <Database className="w-6 h-6 text-gray-600" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center mb-4">
            <Filter className="w-5 h-5 text-gray-400 mr-2" />
            <h3 className="font-medium text-gray-900">Filters</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search roles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              />
            </div>

            <select
              value={filterDataroom}
              onChange={(e) => setFilterDataroom(e.target.value)}
              className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-white hover:border-gray-400 transition-colors appearance-none cursor-pointer shadow-sm"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.25rem'
              }}
            >
              <option value="">All Datarooms</option>
              {datarooms.map(dr => (
                <option key={dr.uuid} value={dr.uuid}>
                  {dr.name}
                </option>
              ))}
            </select>

            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-white hover:border-gray-400 transition-colors appearance-none cursor-pointer shadow-sm"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.25rem'
              }}
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>

            <select
              value={filterPredefined}
              onChange={(e) => setFilterPredefined(e.target.value)}
              className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-white hover:border-gray-400 transition-colors appearance-none cursor-pointer shadow-sm"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.25rem'
              }}
            >
              <option value="">All Types</option>
              <option value="true">Predefined</option>
              <option value="false">Custom</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-white hover:border-gray-400 transition-colors appearance-none cursor-pointer shadow-sm"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.25rem'
              }}
            >
              <option value="name">Sort by Name</option>
              <option value="created_at">Sort by Date</option>
              <option value="user_count">Sort by Users</option>
            </select>

            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-white hover:border-gray-400 transition-colors appearance-none cursor-pointer shadow-sm"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.25rem'
              }}
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
        </div>

        {/* Roles Table */}
        {loading ? (
          <PageLoader />
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <ResponsiveDataTable
              data={roles}
              columns={columns}
              actions={actions}
              emptyMessage="No roles found"
              emptyIcon={Shield}
            />
            
            {/* Pagination */}
            {pagination.last_page > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-700">
                    Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
                    {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of{' '}
                    {pagination.total} results
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
                      disabled={pagination.current_page === 1}
                      className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 hover:bg-gray-50"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
                      disabled={pagination.current_page === pagination.last_page}
                      className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 hover:bg-gray-50"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Create Role Modal */}
        <ResponsiveModal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            resetForm();
          }}
          title="Create New Role"
          icon={Shield}
          maxWidth="max-w-3xl"
        >
          <ResponsiveForm onSubmit={handleCreate}>
            <div className="bg-indigo-50 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <Sparkles className="w-5 h-5 text-indigo-600 mr-2" />
                <h3 className="text-sm font-medium text-gray-900">Create a new role for dataroom management</h3>
              </div>
              <p className="text-xs text-gray-600 mt-1 ml-7">Define permissions and access levels for users</p>
            </div>

            <SearchableSelect
              label="Select Dataroom"
              value={formData.data_room_uuid}
              onChange={(e) => setFormData({ ...formData, data_room_uuid: e.target.value })}
              options={datarooms.map(dr => ({
                value: dr.uuid,
                label: dr.name,
                description: `UUID: ${dr.uuid}`
              }))}
              placeholder="Choose a dataroom"
              searchPlaceholder="Search datarooms..."
              error={formErrors.data_room_uuid}
              required
              optionLabel="label"
              optionValue="value"
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ResponsiveInput
                label="Role Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                error={formErrors.name}
                placeholder="e.g., document-reviewer"
                required
                helperText="Use lowercase with hyphens"
              />
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      checked={formData.is_active === true}
                      onChange={() => setFormData({ ...formData, is_active: true })}
                      className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                    />
                    <span className="ml-2 flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
                      Active
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      checked={formData.is_active === false}
                      onChange={() => setFormData({ ...formData, is_active: false })}
                      className="w-4 h-4 text-red-600 border-gray-300 focus:ring-red-500"
                    />
                    <span className="ml-2 flex items-center">
                      <XCircle className="w-4 h-4 text-red-500 mr-1" />
                      Inactive
                    </span>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (Optional)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="Describe the role's purpose..."
              />
            </div>
            
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Permissions <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => {
                      const allPermIds = permissions.map(p => p.id);
                      setFormData({ ...formData, permissions: allPermIds });
                    }}
                    className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    Select All
                  </button>
                  <span className="text-gray-400">|</span>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, permissions: [] })}
                    className="text-xs text-gray-600 hover:text-gray-700 font-medium"
                  >
                    Clear All
                  </button>
                </div>
              </div>
              {formErrors.permissions && (
                <p className="text-red-500 text-sm mb-2">{formErrors.permissions}</p>
              )}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="max-h-64 overflow-y-auto bg-gray-50">
                  {Object.entries(groupedPermissions).map(([category, perms], index) => (
                    <div key={category} className={`${index > 0 ? 'border-t border-gray-200' : ''}`}>
                      <div className="bg-white px-3 py-1.5 text-sm border-b border-gray-100">
                        <h4 className="font-medium text-gray-900 flex items-center">
                          <Key className="w-4 h-4 mr-2 text-gray-400" />
                          {category}
                        </h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-4">
                        {perms.map(permission => (
                          <label
                            key={permission.id}
                            className="flex items-center p-2 rounded hover:bg-white cursor-pointer transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={formData.permissions.includes(permission.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFormData({
                                    ...formData,
                                    permissions: [...formData.permissions, permission.id]
                                  });
                                } else {
                                  setFormData({
                                    ...formData,
                                    permissions: formData.permissions.filter(id => id !== permission.id)
                                  });
                                }
                              }}
                              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">
                              {permission.display_name || permission.name}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="bg-white px-3 py-1.5 text-sm border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    {formData.permissions.length} permission{formData.permissions.length !== 1 ? 's' : ''} selected
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
              <CancelButton
                type="button"
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
              />
              <SaveButton
                type="submit"
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Create Role'}
              </SaveButton>
            </div>
          </ResponsiveForm>
        </ResponsiveModal>

        {/* Edit Role Modal */}
        <ResponsiveModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            resetForm();
          }}
          title="Edit Role"
          icon={Edit}
          maxWidth="max-w-2xl"
        >
          <ResponsiveForm onSubmit={handleEdit}>
            <ResponsiveSelect
              label="Dataroom"
              value={formData.data_room_uuid}
              onChange={(e) => setFormData({ ...formData, data_room_uuid: e.target.value })}
              error={formErrors.data_room_uuid}
              disabled
            >
              {datarooms.map(dr => (
                <option key={dr.uuid} value={dr.uuid}>
                  {dr.name}
                </option>
              ))}
            </ResponsiveSelect>
            
            <ResponsiveInput
              label="Role Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={formErrors.name}
              required
            />
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (Optional)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Permissions <span className="text-red-500">*</span>
              </label>
              <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-lg p-3">
                {Object.entries(groupedPermissions).map(([category, perms]) => (
                  <div key={category} className="mb-4">
                    <h4 className="font-medium text-gray-900 mb-2">{category}</h4>
                    <div className="space-y-2">
                      {perms.map(permission => (
                        <label key={permission.id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.permissions.includes(permission.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  permissions: [...formData.permissions, permission.id]
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  permissions: formData.permissions.filter(id => id !== permission.id)
                                });
                              }
                            }}
                            className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            {permission.display_name}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <ResponsiveCheckbox
              label="Active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              helperText="Enable or disable this role"
            />
            
            <div className="flex justify-end gap-3 mt-6">
              <CancelButton
                type="button"
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
              />
              <UpdateButton
                type="submit"
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                {isSubmitting ? 'Updating...' : 'Update Role'}
              </UpdateButton>
            </div>
          </ResponsiveForm>
        </ResponsiveModal>

        {/* View Role Modal */}
        <ResponsiveModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setSelectedRole(null);
          }}
          title="Role Details"
          icon={Eye}
          maxWidth="max-w-2xl"
        >
          {selectedRole && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wider">Role Name</label>
                    <p className="font-medium text-gray-900 mt-1">{selectedRole.name}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wider">Dataroom</label>
                    <p className="font-medium text-gray-900 mt-1">
                      {selectedRole.dataroom?.name || 'Unknown'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wider">Type</label>
                    <p className="font-medium text-gray-900 mt-1">
                      {selectedRole.is_predefined ? 'Predefined' : 'Custom'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wider">Status</label>
                    <div className="mt-1">{getStatusBadge(selectedRole.is_active)}</div>
                  </div>
                </div>
              </div>

              {selectedRole.description && (
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider">Description</label>
                  <p className="font-medium text-gray-900 mt-1 bg-gray-50 p-3 rounded-lg">
                    {selectedRole.description}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider">Users</label>
                  <p className="font-medium text-gray-900 mt-1">
                    {selectedRole.user_count || 0} users
                  </p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider">Permissions</label>
                  <p className="font-medium text-gray-900 mt-1">
                    {selectedRole.permission_count || 0} permissions
                  </p>
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider">Created</label>
                <p className="font-medium text-gray-900 mt-1">
                  {new Date(selectedRole.created_at).toLocaleString()}
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                {!selectedRole.is_predefined && (
                  <UpdateButton
                    onClick={() => {
                      setShowViewModal(false);
                      setFormData({
                        data_room_uuid: selectedRole.data_room_uuid,
                        name: selectedRole.name,
                        description: selectedRole.description || '',
                        permissions: selectedRole.permissions?.map(p => p.id) || [],
                        is_active: selectedRole.is_active
                      });
                      setShowEditModal(true);
                    }}
                  >
                    Edit Role
                  </UpdateButton>
                )}
                <CancelButton
                  onClick={() => {
                    setShowViewModal(false);
                    setSelectedRole(null);
                  }}
                >
                  Close
                </CancelButton>
              </div>
            </div>
          )}
        </ResponsiveModal>

        {/* Delete Confirmation Modal */}
        <ResponsiveModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedRole(null);
          }}
          title="Delete Role"
          icon={AlertCircle}
          iconColor="text-red-600"
        >
          <p className="text-gray-600 mb-6">
            Are you sure you want to delete the role "{selectedRole?.name}"? 
            This action cannot be undone and will affect all users with this role.
          </p>
          <div className="flex justify-end gap-3">
            <CancelButton
              onClick={() => {
                setShowDeleteModal(false);
                setSelectedRole(null);
              }}
            />
            <DeleteButton
              onClick={handleDelete}
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              {isSubmitting ? 'Deleting...' : 'Delete Role'}
            </DeleteButton>
          </div>
        </ResponsiveModal>
      </div>
    </div>
  );
};

export default SuperAdminRoles;